import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import {
    BaseItem as ZhBaseItem,
    DateRangePicker as ZhDateRangePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Dialog as ZhDialog,
    Image as ZhImage,
    Table as ZhTable,
    Upload as ZhUpload,
    type TableColumnProps,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import { dateTimeFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import type {
    CampaignDemandSubmitVo,
    CampaignRegistrationForm,
    CampaignRegistrationVo,
    CustomerInvoiceHeaderVo,
    File as AdminFile
} from "@zhihaoscm/entity";
import { formatterPhone } from "@zhihaoscm/utils";
import {
    assignActivity as defaultAssignActivity,
    loadActivitySpecials as defaultLoadActivitySpecials
} from "./ActivityPage";
import {
    getActivitySpecialProfile,
    type ActivitySpecial,
    type ActivityProductRow,
    type ActivityProductPageResult,
    type ActivityProductQuery,
    type ActivityRegistrationConfig,
    type ActivityRow
} from "./types";

const activityContentLabels: Record<number, string> = { 1: "活动报名", 2: "活动商品" };
const participationLabels: Record<number, string> = { 1: "冠名", 2: "协办", 3: "赞助", 4: "参展", 5: "普通参会" };
const activityStateLabels: Record<number, string> = { 0: "已关闭", 1: "已开启", 2: "草稿" };
const invoiceHeaderTypeLabels: Record<number, string> = { 1: "企业", 2: "个人或事业单位" };
const invoiceTypeLabels: Record<number, string> = { 1: "普通发票", 2: "增值税发票" };
const registrationMeetingLabels: Record<number, string> = {
    1: "冠名",
    2: "协办",
    3: "赞助",
    4: "参展",
    5: "普通参会"
};
const registrationStateLabels: Record<number, string> = {
    1: "待支付",
    2: "待确认支付",
    3: "已报名",
    4: "已取消"
};

/** 活动报名凭证沿用 Vue file-list 的文件类型集合和 50MB 单文件限制。 */
const activityVoucherAccept = [
    ".zip",
    ".rar",
    ".7z",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif"
].join(",");
const activityVoucherMaxSize = 50 * 1024 * 1024;
const activityVoucherFileExtensions = new Set([
    "zip",
    "rar",
    "7z",
    "doc",
    "docx",
    "xls",
    "xlsx",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "webp",
    "avif"
]);
const activityVoucherFileMimeTypes = new Set([
    "application/x-zip-compressed",
    "application/zip",
    "application/x-rar-compressed",
    "application/x-7z-compressed",
    "application/x-compressed",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif"
]);

/** 将文本框中的文件 ID 标准化，上传成功后与手工录入 ID 合并。 */
const parseActivityFileIds = (value: string) =>
    value
        .split(",")
        .map(item => Number(item.trim()))
        .filter(item => Number.isInteger(item) && item > 0);

/** 按 Vue 报名状态决定管理操作的可见性，具体业务弹窗在后续任务接入。 */
const getRegistrationOperationState = (state?: number) => ({
    canConfirmPayment: state === 1 || state === 2,
    canEdit: state === 1 || state === 2 || state === 3,
    canCancel: state === 1 || state === 2 || state === 3,
    canUploadInvoice: state === 3
});

/** 按 Vue 详情页规则生成正式/草稿活动的 tabs 入口。 */
const getActivityDetailTabs = (state?: number, contentConfigs?: number[]) => {
    if (state === 2) {
        return [
            { key: "base", label: "基本信息" },
            ...(contentConfigs?.includes(2) ? [{ key: "product", label: "商品信息" }] : [])
        ];
    }
    return [
        { key: "base", label: "基本信息" },
        ...(contentConfigs?.includes(1) ? [{ key: "registration", label: "报名信息" }] : []),
        { key: "invoice", label: "发票抬头" },
        { key: "requirement", label: "需求对接" },
        ...(contentConfigs?.includes(2) ? [{ key: "product", label: "商品信息" }] : [])
    ];
};

/** 将 Vue 活动配置编码转换为详情页可读文本。 */
const formatActivityConfigs = (values?: number[]) =>
    values?.length ? values.map(value => activityContentLabels[value] || String(value)).join("、") : "-";

/** 将 Vue 文件对象转换为稳定的详情展示名称。 */
const formatActivityFileNames = (
    files?: Array<{ id?: number | string; name?: string; path?: string; url?: string }>
) =>
    files?.length
        ? files.map(file => file.name || file.path || file.url || (file.id == null ? "-" : `文件${file.id}`)).join("、")
        : "-";

/** 复用 Vue 报名配置卡片的字段和文案，详情模式只读展示。 */
function ActivityRegistrationCard({ config }: { config: ActivityRegistrationConfig }) {
    const participationName = participationLabels[config.type || 0] || `参会类型${config.type || "-"}`;
    const feeUnit = config.type === 5 ? "元/人" : "元";
    const giftDurationUnit = config.giveDurationUnit === 1 ? "月" : "天";

    return (
        <Card className="admin-detail-subcard mb-3" size="small" title={participationName}>
            <ZhGrid cols={1} gap={[12, 12]}>
                <ZhBaseItem
                    label="报名费"
                    value={config.registrationFee == null ? "-" : `报名费：${config.registrationFee} ${feeUnit}`}
                />
                {config.type !== 5 ? (
                    <ZhBaseItem
                        label="参会最大人数"
                        value={config.maxParticipants ? `参会最大人数：${config.maxParticipants}人` : "-"}
                    />
                ) : null}
                <ZhBaseItem
                    label="字段配置"
                    value={
                        config.fieldConfig?.length
                            ? config.fieldConfig.map(field => (
                                  <div key={`${field.fieldName}-${field.hasRequired ? "required" : "optional"}`}>
                                      {field.fieldName || "-"}：{field.hasRequired ? "必填" : "选填"}
                                  </div>
                              ))
                            : "-"
                    }
                />
                <ZhBaseItem
                    label="权益介绍"
                    value={
                        config.rightsIntroduction ? (
                            <div dangerouslySetInnerHTML={{ __html: config.rightsIntroduction }} />
                        ) : (
                            "-"
                        )
                    }
                />
                <ZhBaseItem label="是否赠送会员" value={config.isGiveMember === 1 ? "赠送会员：是" : "赠送会员：否"} />
                {config.isGiveMember === 1 ? (
                    <>
                        <ZhBaseItem
                            label="赠送会员类型"
                            value={`赠送会员类型：${config.giveMemberType === 1 ? "基础会员" : "高级会员"}`}
                        />
                        <ZhBaseItem
                            label="赠送时长"
                            value={config.giveDuration ? `赠送时长：${config.giveDuration}${giftDurationUnit}` : "-"}
                        />
                        <ZhBaseItem
                            label="赠送个数"
                            value={config.giveCount ? `赠送个数：${config.giveCount}个` : "-"}
                        />
                    </>
                ) : null}
            </ZhGrid>
        </Card>
    );
}

export interface ActivityDetailPageProps {
    assignActivity?: (id: number | string, handlerId: number | string) => Promise<void>;
    cancelRegistration?: (id: number | string) => Promise<void>;
    confirmPayment?: (params: { id: number | string; fileIds: number[] }) => Promise<void>;
    updateRegistrationInfo?: (params: CampaignRegistrationForm) => Promise<void>;
    uploadInvoice?: (params: { id: number | string; invoiceIds: number[] }) => Promise<void>;
    closeActivity?: (id: number | string) => Promise<void>;
    deleteActivity?: (id: number | string) => Promise<void>;
    loadActivity?: (id: number | string) => Promise<ActivityRow>;
    loadInvoiceHeaders?: (
        id: number | string,
        query: ActivityInvoiceHeaderQuery
    ) => Promise<ActivityInvoiceHeaderPageResult>;
    loadRequirements?: (id: number | string, query: ActivityRequirementQuery) => Promise<ActivityRequirementPageResult>;
    loadRegistrations?: (
        id: number | string,
        query: ActivityRegistrationQuery
    ) => Promise<ActivityRegistrationPageResult>;
    loadRegistrationCompanyHeaders?: (query: ActivityCompanyHeaderQuery) => Promise<ActivityCompanyHeaderPageResult>;
    loadRegistrationFiles?: (ids: number[]) => Promise<AdminFile[]>;
    loadActivityFiles?: (ids: number[]) => Promise<AdminFile[]>;
    loadProducts?: (id: number | string, query: ActivityProductQuery) => Promise<ActivityProductPageResult>;
    loadSpecials?: (keyword: string) => Promise<ActivitySpecial[]>;
    openActivity?: (id: number | string) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 活动详情接口。 */
export async function loadActivityDetail(id: number | string): Promise<ActivityRow> {
    const response = await request.service.get<ActivityRow>(`/admin/campaign/vo/${id}`);
    return response.data;
}

/** 复用 Vue 活动详情的状态操作接口。 */
export async function openActivityDetail(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/open/${id}`);
}

/** 复用 Vue 取消报名接口。 */
export async function cancelActivityRegistration(id: number | string): Promise<void> {
    await request.service.post(`/admin/campaign-registration/cancel?id=${id}`);
}

/** 复用 Vue 确认收款接口，沿用报名 ID 和支付凭证 ID 集合。 */
export async function confirmActivityRegistrationPayment(params: {
    id: number | string;
    fileIds: number[];
}): Promise<void> {
    await request.service.post("/admin/campaign-registration/confirm-pay-file", params);
}

/** 复用 Vue 修改报名信息接口，保留报名表单字段和详情子表单结构。 */
export async function updateActivityRegistrationInfo(params: CampaignRegistrationForm): Promise<void> {
    await request.service.post("/admin/campaign-registration/update", params);
}

/** 复用 Vue 上传发票接口，当前弹窗仅接收已上传文件 ID。 */
export async function uploadActivityRegistrationInvoice(params: {
    id: number | string;
    invoiceIds: number[];
}): Promise<void> {
    await request.service.put("/admin/campaign-registration/invoice", params);
}

export async function closeActivityDetail(id: number | string): Promise<void> {
    await request.service.put(`/admin/campaign/close/${id}`);
}

export async function deleteActivityDetail(id: number | string): Promise<void> {
    await request.service.delete(`/admin/campaign/delete/${id}`);
}

/** 复用 Vue 活动详情关联商品分页接口。 */
export async function loadActivityProducts(
    id: number | string,
    query: ActivityProductQuery
): Promise<ActivityProductPageResult> {
    const response = await request.service.get<ActivityProductPageResult>(`/admin/campaign/products-paging?id=${id}`, {
        params: query
    });
    return response.data;
}

export interface ActivityInvoiceHeaderRow extends Partial<CustomerInvoiceHeaderVo> {
    customerInvoiceHeader?: CustomerInvoiceHeaderVo;
    submitterName?: string;
    submitterMobile?: string;
}

export interface ActivityInvoiceHeaderPageResult {
    list: ActivityInvoiceHeaderRow[];
    total: number;
}

export interface ActivityInvoiceHeaderQuery {
    page: number;
    size: number;
}

export interface ActivityRequirementRow extends CampaignDemandSubmitVo {}

export interface ActivityRequirementPageResult {
    list: ActivityRequirementRow[];
    total: number;
}

export interface ActivityRequirementQuery {
    page: number;
    size: number;
    demandNo?: string;
    keyword?: string;
    submitStartTime?: string;
    submitEndTime?: string;
}

type ActivityRequirementFilterValues = Pick<ActivityRequirementQuery, "demandNo" | "keyword"> & {
    updatedTime?: [string, string] | null;
};

export interface ActivityRegistrationPageResult {
    list: CampaignRegistrationVo[];
    total: number;
}

export interface ActivityRegistrationQuery {
    page: number;
    size: number;
    registrationNo?: string;
    keyword?: string;
    mobile?: string;
    submitterKeyword?: string;
    meetingType?: number;
    state?: number;
}

export interface ActivityCompanyHeaderQuery {
    page: number;
    size: number;
    name?: string;
}

export interface ActivityCompanyHeaderPageResult {
    list: Array<{ companyCode?: string; companyName?: string }>;
    total?: number;
}

/** 复用 Vue file-dialog 的文件批量查询接口。 */
export async function loadActivityRegistrationFiles(ids: number[]): Promise<AdminFile[]> {
    const response = await request.service.post<AdminFile[]>("/dataService/admin/file/ids", ids);
    return response.data;
}

/** 复用 Vue 发票抬头分页接口，活动 ID 作为 campaignId 查询条件。 */
export async function loadActivityInvoiceHeaders(
    campaignId: number | string,
    query: ActivityInvoiceHeaderQuery
): Promise<ActivityInvoiceHeaderPageResult> {
    const response = await request.service.get<ActivityInvoiceHeaderPageResult>(
        "/admin/customer/invoice/header/paging-campaign",
        { params: { campaignId, ...query } }
    );
    return response.data;
}

/** 兼容 Vue 发票列表的嵌套 customerInvoiceHeader 返回结构。 */
function getInvoiceHeader(row: ActivityInvoiceHeaderRow): Partial<CustomerInvoiceHeaderVo> {
    return row.customerInvoiceHeader || row;
}

/** 复用 Vue 需求对接分页接口，活动 ID 作为 campaignId 查询条件。 */
export async function loadActivityRequirements(
    campaignId: number | string,
    query: ActivityRequirementQuery
): Promise<ActivityRequirementPageResult> {
    const response = await request.service.get<ActivityRequirementPageResult>("/admin/campaign-demand/paging", {
        params: { campaignId, ...query }
    });
    return response.data;
}

/** 复用 Vue 报名信息分页接口，活动 ID 作为 campaignId 查询条件。 */
export async function loadActivityRegistrations(
    campaignId: number | string,
    query: ActivityRegistrationQuery
): Promise<ActivityRegistrationPageResult> {
    const response = await request.service.get<ActivityRegistrationPageResult>("/admin/campaign-registration/paging", {
        params: { campaignId, ...query }
    });
    return response.data;
}

/** 复用 Vue 企业抬头远程候选接口，编辑报名时按名称分页查询。 */
export async function loadActivityRegistrationCompanyHeaders(
    query: ActivityCompanyHeaderQuery
): Promise<ActivityCompanyHeaderPageResult> {
    const response = await request.service.get<ActivityCompanyHeaderPageResult>(
        "/admin/campaign-registration/company-head-selector",
        { params: query }
    );
    return response.data;
}

interface ActivityProductFilterValues {
    keyword?: string;
    productTypeName?: string;
    supplierName?: string;
    type?: string;
    listingState?: string;
    addTime?: [string | null, string | null];
}

interface ActivityRegistrationFilterValues {
    registrationNo?: string;
    keyword?: string;
    mobile?: string;
    submitterKeyword?: string;
    meetingType?: string;
    state?: string;
}

/** 将详情页筛选值转换为 Vue 商品分页接口使用的字段和日期格式。 */
function toActivityProductQuery(values: ActivityProductFilterValues): Omit<ActivityProductQuery, "page" | "size"> {
    const query: Omit<ActivityProductQuery, "page" | "size"> = {};
    if (values.keyword?.trim()) query.keyword = values.keyword.trim();
    if (values.productTypeName?.trim()) query.productTypeName = values.productTypeName.trim();
    if (values.supplierName?.trim()) query.supplierName = values.supplierName.trim();
    if (values.type) query.type = Number(values.type);
    if (values.listingState) query.listingState = Number(values.listingState);
    if (values.addTime?.[0]) query.startTime = `${values.addTime[0]} 00:00:00`;
    if (values.addTime?.[1]) query.endTime = `${values.addTime[1]} 23:59:59`;
    return query;
}

const emptyActivityProductFilters: Omit<ActivityProductQuery, "page" | "size"> = {};
const emptyActivityRegistrationFilters: Omit<ActivityRegistrationQuery, "page" | "size"> = {};

/** 商品活动详情页，展示 Vue 详情页中的基础活动字段。 */
export function ActivityDetailPage({
    closeActivity = closeActivityDetail,
    cancelRegistration = cancelActivityRegistration,
    confirmPayment = confirmActivityRegistrationPayment,
    updateRegistrationInfo = updateActivityRegistrationInfo,
    uploadInvoice = uploadActivityRegistrationInvoice,
    deleteActivity = deleteActivityDetail,
    loadActivity = loadActivityDetail,
    loadInvoiceHeaders = loadActivityInvoiceHeaders,
    loadRequirements = loadActivityRequirements,
    loadRegistrations = loadActivityRegistrations,
    loadRegistrationCompanyHeaders = loadActivityRegistrationCompanyHeaders,
    loadRegistrationFiles = loadActivityRegistrationFiles,
    loadActivityFiles = loadActivityRegistrationFiles,
    loadProducts = loadActivityProducts,
    loadSpecials = defaultLoadActivitySpecials,
    openActivity = openActivityDetail,
    assignActivity = defaultAssignActivity,
    permissions
}: ActivityDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite = (permissions || storePermissions).includes("ROLE_ACTI_W");
    const id = new URLSearchParams(location.search).get("id");
    const [campaign, setCampaign] = useState<ActivityRow["campaign"]>();
    const [activityDetail, setActivityDetail] = useState<ActivityRow>();
    const [activityMainImages, setActivityMainImages] = useState<{
        pc?: AdminFile;
        mobile?: AdminFile;
    }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [activeDetailTab, setActiveDetailTab] = useState("base");
    const [specials, setSpecials] = useState<ActivitySpecial[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [specialKeyword, setSpecialKeyword] = useState("");
    const [products, setProducts] = useState<NonNullable<ActivityProductPageResult["list"]>>([]);
    const [productTotal, setProductTotal] = useState(0);
    const [productPage, setProductPage] = useState(1);
    const [productPageSize, setProductPageSize] = useState(10);
    const [productFilters, setProductFilters] =
        useState<Omit<ActivityProductQuery, "page" | "size">>(emptyActivityProductFilters);
    const [productForm] = Form.useForm<ActivityProductFilterValues>();
    const [invoiceHeaders, setInvoiceHeaders] = useState<ActivityInvoiceHeaderRow[]>([]);
    const [invoiceTotal, setInvoiceTotal] = useState(0);
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoicePageSize, setInvoicePageSize] = useState(10);
    const [requirements, setRequirements] = useState<ActivityRequirementRow[]>([]);
    const [requirementTotal, setRequirementTotal] = useState(0);
    const [requirementPage, setRequirementPage] = useState(1);
    const [requirementPageSize, setRequirementPageSize] = useState(10);
    const [requirementFilters, setRequirementFilters] = useState<Omit<ActivityRequirementQuery, "page" | "size">>({});
    const [requirementForm] = Form.useForm<ActivityRequirementFilterValues>();
    const [registrations, setRegistrations] = useState<CampaignRegistrationVo[]>([]);
    const [registrationTotal, setRegistrationTotal] = useState(0);
    const [registrationPage, setRegistrationPage] = useState(1);
    const [registrationPageSize, setRegistrationPageSize] = useState(10);
    const [registrationFilters, setRegistrationFilters] = useState<Omit<ActivityRegistrationQuery, "page" | "size">>(
        emptyActivityRegistrationFilters
    );
    const [registrationForm] = Form.useForm<ActivityRegistrationFilterValues>();
    const [registrationPreviewOpen, setRegistrationPreviewOpen] = useState(false);
    const [registrationPreviewTitle, setRegistrationPreviewTitle] = useState("查看凭证");
    const [registrationPreviewFiles, setRegistrationPreviewFiles] = useState<AdminFile[]>([]);
    const [registrationPreviewError, setRegistrationPreviewError] = useState<string | null>(null);
    const [paymentVoucherUploadOpen, setPaymentVoucherUploadOpen] = useState(false);
    const [paymentVoucherUploadRegistrationId, setPaymentVoucherUploadRegistrationId] = useState<number | string>();
    const [paymentVoucherUploadIds, setPaymentVoucherUploadIds] = useState("");
    const [paymentVoucherUploadError, setPaymentVoucherUploadError] = useState<string | null>(null);
    const [paymentVoucherUploading, setPaymentVoucherUploading] = useState(false);
    const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
    const [invoiceUploadRegistrationId, setInvoiceUploadRegistrationId] = useState<number | string>();
    const [invoiceUploadIds, setInvoiceUploadIds] = useState("");
    const [invoiceUploadError, setInvoiceUploadError] = useState<string | null>(null);
    const [invoiceUploading, setInvoiceUploading] = useState(false);
    const [registrationEditOpen, setRegistrationEditOpen] = useState(false);
    const [registrationEditRow, setRegistrationEditRow] = useState<CampaignRegistrationVo>();
    const [registrationEditMeetingType, setRegistrationEditMeetingType] = useState<number>();
    const [registrationEditFee, setRegistrationEditFee] = useState<number>();
    const [registrationEditCompany, setRegistrationEditCompany] = useState("");
    const [registrationCompanyHeaderOptions, setRegistrationCompanyHeaderOptions] = useState<
        Array<{ companyCode?: string; companyName?: string }>
    >([]);
    const [registrationEditRequirements, setRegistrationEditRequirements] = useState("");
    const [registrationEditError, setRegistrationEditError] = useState<string | null>(null);
    const [registrationEditApplicants, setRegistrationEditApplicants] = useState<
        Array<{ id?: string; position: string; name: string; mobile: string }>
    >([]);

    useEffect(() => {
        if (!id) return;
        void loadActivity(id)
            .then(async result => {
                // Vue 详情接口的枚举可能以字符串返回，加载时统一成数字供 Tab、权限和字段分支复用。
                const normalizedCampaign: ActivityRow["campaign"] = {
                    ...result.campaign,
                    state: result.campaign.state == null ? undefined : Number(result.campaign.state),
                    contentConfigs: result.campaign.contentConfigs?.map(config => Number(config)),
                    meetingParticipationConfigs: result.campaign.meetingParticipationConfigs?.map(config =>
                        Number(config)
                    ),
                    needAgreeProtocol:
                        result.campaign.needAgreeProtocol == null
                            ? undefined
                            : Number(result.campaign.needAgreeProtocol)
                };
                // Vue 报名配置卡片的枚举也可能以字符串返回，统一后再交给只读卡片渲染。
                const normalizedConfigs = result.configs?.map(config => ({
                    ...config,
                    type: config.type == null ? undefined : Number(config.type),
                    fieldConfig: config.fieldConfig?.map(field => ({
                        ...field,
                        hasRequired: field.hasRequired == null ? undefined : Number(field.hasRequired) === 1
                    })),
                    isGiveMember: config.isGiveMember == null ? undefined : Number(config.isGiveMember),
                    giveMemberType: config.giveMemberType == null ? undefined : Number(config.giveMemberType),
                    giveDurationUnit: config.giveDurationUnit == null ? undefined : Number(config.giveDurationUnit)
                }));
                const normalizedResult: ActivityRow = {
                    ...result,
                    campaign: normalizedCampaign,
                    configs: normalizedConfigs
                };
                // 旧 Vue activeBasePanel 会按两个主图 ID 批量查询文件，再交给图片组件展示。
                try {
                    const mainImageIds = [normalizedCampaign.pcImgMainId, normalizedCampaign.mobileImgMainId]
                        .map(value => Number(value))
                        .filter(value => Number.isInteger(value) && value > 0);
                    const mainImageFiles = mainImageIds.length ? await loadActivityFiles(mainImageIds) : [];
                    const mainImageById = new Map(mainImageFiles.map(file => [Number(file.id), file]));
                    setActivityMainImages({
                        pc: mainImageById.get(Number(normalizedCampaign.pcImgMainId)),
                        mobile: mainImageById.get(Number(normalizedCampaign.mobileImgMainId))
                    });
                } catch {
                    // 主图查询失败不阻断活动详情其余字段，保持旧 Vue 详情的降级行为。
                    setActivityMainImages({});
                }
                setErrorMessage(null);
                setActivityDetail(normalizedResult);
                setCampaign(normalizedCampaign);
                setActiveDetailTab("base");
            })
            .catch(() => setErrorMessage("活动详情加载失败"));
    }, [id, loadActivity]);

    useEffect(() => {
        if (campaign?.id == null || !campaign.contentConfigs?.includes(2)) {
            setProducts([]);
            setProductTotal(0);
            setProductPage(1);
            return;
        }
        let disposed = false;
        void loadProducts(campaign.id, { page: productPage, size: productPageSize, ...productFilters })
            .then(result => {
                if (!disposed) {
                    // Vue 商品分页接口的类型和上架状态可能以字符串返回，加载时统一数字化供列展示和详情入口复用。
                    const normalizedProducts = (result.list || []).map(product => ({
                        ...product,
                        type: product.type == null ? undefined : Number(product.type),
                        product: product.product
                            ? {
                                  ...product.product,
                                  state: product.product.state == null ? undefined : Number(product.product.state)
                              }
                            : product.product
                    }));
                    setProducts(normalizedProducts);
                    setProductTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("活动商品加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [campaign, loadProducts, productFilters, productPage, productPageSize]);

    useEffect(() => {
        if (activeDetailTab !== "invoice" || campaign?.id == null) return;
        let disposed = false;
        void loadInvoiceHeaders(campaign.id, { page: invoicePage, size: invoicePageSize })
            .then(result => {
                if (!disposed) {
                    // Vue 发票列表枚举可能以字符串返回，入口统一数字化后再复用类型文案。
                    const normalizedInvoiceHeaders = (result.list || []).map(row => ({
                        ...row,
                        headerType: row.headerType == null ? undefined : Number(row.headerType),
                        invoiceType: row.invoiceType == null ? undefined : Number(row.invoiceType),
                        customerInvoiceHeader: row.customerInvoiceHeader
                            ? {
                                  ...row.customerInvoiceHeader,
                                  ...(row.customerInvoiceHeader.headerType == null
                                      ? {}
                                      : { headerType: Number(row.customerInvoiceHeader.headerType) }),
                                  ...(row.customerInvoiceHeader.invoiceType == null
                                      ? {}
                                      : { invoiceType: Number(row.customerInvoiceHeader.invoiceType) })
                              }
                            : row.customerInvoiceHeader
                    }));
                    setInvoiceHeaders(normalizedInvoiceHeaders);
                    setInvoiceTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("活动发票抬头加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [activeDetailTab, campaign?.id, invoicePage, invoicePageSize, loadInvoiceHeaders]);

    useEffect(() => {
        if (activeDetailTab !== "requirement" || campaign?.id == null) return;
        let disposed = false;
        void loadRequirements(campaign.id, {
            page: requirementPage,
            size: requirementPageSize,
            ...requirementFilters
        })
            .then(result => {
                if (!disposed) {
                    setRequirements(result.list || []);
                    setRequirementTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("活动需求对接加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [activeDetailTab, campaign?.id, loadRequirements, requirementFilters, requirementPage, requirementPageSize]);

    // 将 Vue 需求对接表格搜索条件转换为分页接口使用的字段，并统一提交日期边界。
    const searchRequirements = (values: ActivityRequirementFilterValues) => {
        const { updatedTime, ...rawFilters } = values;
        const [startDate, endDate] = updatedTime || [];
        const filters = Object.fromEntries(
            Object.entries(rawFilters).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ) as Omit<ActivityRequirementQuery, "page" | "size">;
        if (startDate) filters.submitStartTime = `${startDate} 00:00:00`;
        if (endDate) filters.submitEndTime = `${endDate} 23:59:59`;
        setRequirementFilters(filters);
        setRequirementPage(1);
    };

    const resetRequirementSearch = () => {
        requirementForm.resetFields();
        setRequirementFilters({});
        setRequirementPage(1);
    };

    useEffect(() => {
        if (activeDetailTab !== "registration" || campaign?.id == null) return;
        let disposed = false;
        void loadRegistrations(campaign.id, {
            page: registrationPage,
            size: registrationPageSize,
            ...registrationFilters
        })
            .then(result => {
                if (!disposed) {
                    // Vue 报名接口的状态和参会形式可能以字符串返回，列表入口统一数字化供操作权限和文案复用。
                    const normalizedRegistrations = (result.list || []).map(registration => ({
                        ...registration,
                        campaignRegistration: registration.campaignRegistration
                            ? {
                                  ...registration.campaignRegistration,
                                  meetingType: Number(registration.campaignRegistration.meetingType),
                                  state: Number(registration.campaignRegistration.state)
                              }
                            : registration.campaignRegistration,
                        meetingTypeConfig: registration.meetingTypeConfig?.map(config => ({
                            ...config,
                            type: Number(config.type),
                            fieldConfig: config.fieldConfig?.map(field => ({
                                ...field,
                                hasRequired: Number(field.hasRequired)
                            }))
                        }))
                    }));
                    setRegistrations(normalizedRegistrations);
                    setRegistrationTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("活动报名信息加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [activeDetailTab, campaign?.id, loadRegistrations, registrationFilters, registrationPage, registrationPageSize]);

    // 只有点击查询时才提交筛选，避免用户输入过程中触发多余请求。
    const searchProducts = (values: ActivityProductFilterValues) => {
        setProductFilters(toActivityProductQuery(values));
        setProductPage(1);
    };

    const resetProductFilters = () => {
        productForm.resetFields();
        setProductFilters({});
        setProductPage(1);
    };

    // 将 Vue 报名列表筛选值转换为报名分页接口参数，并在查询时回到第一页。
    const searchRegistrations = (values: ActivityRegistrationFilterValues) => {
        const filters: Omit<ActivityRegistrationQuery, "page" | "size"> = {};
        ["registrationNo", "keyword", "mobile", "submitterKeyword"].forEach(field => {
            const value = values[field as keyof ActivityRegistrationFilterValues];
            if (typeof value === "string" && value.trim()) {
                filters[field as "registrationNo" | "keyword" | "mobile" | "submitterKeyword"] = value.trim();
            }
        });
        if (values.meetingType) filters.meetingType = Number(values.meetingType);
        if (values.state) filters.state = Number(values.state);
        setRegistrationFilters(filters);
        setRegistrationPage(1);
    };

    const resetRegistrationFilters = () => {
        registrationForm.resetFields();
        setRegistrationFilters({});
        setRegistrationPage(1);
    };

    // 沿用 Vue file-dialog：点击凭证后按文件 ID 加载详情并打开查看弹窗。
    const previewRegistrationFiles = async (ids: number[], title: string) => {
        setRegistrationPreviewTitle(title);
        setRegistrationPreviewOpen(true);
        setRegistrationPreviewError(null);
        try {
            setRegistrationPreviewFiles(await loadRegistrationFiles(ids));
        } catch {
            setRegistrationPreviewFiles([]);
            setRegistrationPreviewError("文件信息加载失败");
        }
    };

    // 按 Vue 规则确认取消报名，并在成功后刷新当前报名分页数据。
    const cancelRegistrationRow = (row: CampaignRegistrationVo) => {
        const registrationId = row.campaignRegistration?.id;
        if (registrationId == null) return;
        Modal.confirm({
            cancelText: "取消",
            okText: "确认",
            title: "提示",
            content: "是否确认取消报名",
            onOk: async () => {
                try {
                    await cancelRegistration(registrationId);
                    if (campaign?.id == null) return;
                    const result = await loadRegistrations(campaign.id, {
                        page: registrationPage,
                        size: registrationPageSize,
                        ...registrationFilters
                    });
                    setRegistrations(result.list || []);
                    setRegistrationTotal(result.total || 0);
                } catch {
                    // 取消报名失败时保留当前报名列表，并反馈 Vue 页面同类操作错误。
                    setErrorMessage("取消报名失败");
                }
            }
        });
    };

    // 按 Vue 规则使用已有支付凭证提交确认收款，并在成功后刷新当前分页数据。
    const confirmRegistrationPayment = (row: CampaignRegistrationVo) => {
        const registrationId = row.campaignRegistration?.id;
        const fileIds = (row.campaignRegistration?.paymentVoucherFileIds || []).map(Number);
        if (registrationId == null) return;
        if (!fileIds.length) {
            setPaymentVoucherUploadRegistrationId(registrationId);
            setPaymentVoucherUploadIds("");
            setPaymentVoucherUploadError(null);
            setPaymentVoucherUploadOpen(true);
            return;
        }
        Modal.confirm({
            cancelText: "取消",
            okText: "确认",
            title: "提示",
            content: "是否确认提交支付凭证",
            onOk: async () => {
                try {
                    await confirmPayment({ id: registrationId, fileIds });
                    if (campaign?.id == null) return;
                    const result = await loadRegistrations(campaign.id, {
                        page: registrationPage,
                        size: registrationPageSize,
                        ...registrationFilters
                    });
                    setRegistrations(result.list || []);
                    setRegistrationTotal(result.total || 0);
                } catch {
                    // 确认收款失败时保留当前报名列表，并反馈 Vue 页面同类操作错误。
                    setErrorMessage("确认收款失败");
                }
            }
        });
    };

    /** 上传活动报名凭证，并把 fileService 返回的 ID 回填到原有文本字段。 */
    const uploadActivityVoucherFiles = async (
        files: globalThis.File[],
        currentValue: string,
        setValue: (value: string) => void,
        setError: (message: string | null) => void,
        setUploading: (value: boolean) => void,
        invalidTypeMessage = "文件类型不受支持"
    ) => {
        const currentIds = parseActivityFileIds(currentValue);
        if (currentIds.length + files.length > 10) {
            setError("支付凭证最多上传10个文件");
            return false;
        }
        const invalidType = files.find(file => {
            const extension = file.name.split(".").pop()?.toLowerCase();
            return (
                !extension ||
                !activityVoucherFileExtensions.has(extension) ||
                !activityVoucherFileMimeTypes.has(file.type)
            );
        });
        if (invalidType) {
            setError(invalidTypeMessage);
            return false;
        }
        const invalidFile = files.find(file => file.size > activityVoucherMaxSize);
        if (invalidFile) {
            setError("每个文件不可超过50MB");
            return false;
        }

        setError(null);
        setUploading(true);
        try {
            const uploadedIds = [...currentIds];
            // 按 Vue file-list 的行为逐个上传，任何失败都保留已成功回填的 ID，便于用户重试。
            for (const file of files) {
                const formData = new FormData();
                formData.append("file", file);
                formData.append("fileName", file.name);
                // 文件服务只在用户上传活动凭证时加载，避免详情只读场景被上传模块导入阻塞。
                const { fileService } = await import("@/api/services/fileService");
                const uploadedFile = await fileService.uploadFile(formData);
                const fileId = Number(uploadedFile.id);
                if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
                uploadedIds.push(fileId);
                setValue(uploadedIds.join(","));
            }
            return true;
        } catch {
            setError("文件上传失败");
            return false;
        } finally {
            setUploading(false);
        }
    };

    /** 处理支付凭证上传，上传成功后仍由原提交函数构造 confirmPayment payload。 */
    const handlePaymentVoucherUpload = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        const uploaded = await uploadActivityVoucherFiles(
            [file],
            paymentVoucherUploadIds,
            setPaymentVoucherUploadIds,
            setPaymentVoucherUploadError,
            setPaymentVoucherUploading,
            "支付凭证文件类型不受支持"
        );
        if (uploaded) onSuccess({});
        else onError(new Error("支付凭证上传失败"));
    };

    /** 处理发票上传，沿用 Vue 发票弹窗最多 10 个文件的限制和 ID 回填。 */
    const handleInvoiceUpload = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        const uploaded = await uploadActivityVoucherFiles(
            [file],
            invoiceUploadIds,
            setInvoiceUploadIds,
            setInvoiceUploadError,
            setInvoiceUploading,
            "发票文件仅支持压缩包、文档、表格、PDF或图片格式"
        );
        if (uploaded) onSuccess({});
        else onError(new Error("发票上传失败"));
    };

    // 沿用 Vue 最多 10 个文件的校验，并继续调用原确认收款 payload。
    const submitPaymentVoucherUpload = () => {
        if (paymentVoucherUploadRegistrationId == null) return;
        if (paymentVoucherUploading) return;
        const fileIds = parseActivityFileIds(paymentVoucherUploadIds);
        if (!fileIds.length) {
            setPaymentVoucherUploadError("请上传支付凭证");
            return;
        }
        if (fileIds.length > 10) {
            setPaymentVoucherUploadError("支付凭证最多上传10个文件");
            return;
        }
        Modal.confirm({
            cancelText: "取消",
            okText: "确认",
            title: "提示",
            content: "是否确认提交支付凭证",
            onOk: async () => {
                try {
                    await confirmPayment({ id: paymentVoucherUploadRegistrationId, fileIds });
                    if (campaign?.id == null) return;
                    const result = await loadRegistrations(campaign.id, {
                        page: registrationPage,
                        size: registrationPageSize,
                        ...registrationFilters
                    });
                    setRegistrations(result.list || []);
                    setRegistrationTotal(result.total || 0);
                    setPaymentVoucherUploadOpen(false);
                } catch {
                    // 上传凭证后的确认收款失败时保留支付凭证弹窗，并反馈 Vue 页面同类操作错误。
                    setPaymentVoucherUploadError("确认收款失败");
                }
            }
        });
    };

    // 沿用 Vue 上传发票弹窗的提交边界，先接收文件 ID，再调用业务接口刷新列表。
    const openInvoiceUpload = (row: CampaignRegistrationVo) => {
        const registrationId = row.campaignRegistration?.id;
        if (registrationId == null) return;
        setInvoiceUploadRegistrationId(registrationId);
        setInvoiceUploadIds((row.campaignRegistration?.invoiceFileIds || []).join(","));
        setInvoiceUploadError(null);
        setInvoiceUploadOpen(true);
    };

    const submitInvoiceUpload = async () => {
        if (invoiceUploadRegistrationId == null) return;
        if (invoiceUploading) return;
        const invoiceIds = parseActivityFileIds(invoiceUploadIds);
        if (!invoiceIds.length) {
            setInvoiceUploadError("请填写发票文件 ID");
            return;
        }
        try {
            await uploadInvoice({ id: invoiceUploadRegistrationId, invoiceIds });
            if (campaign?.id != null) {
                const result = await loadRegistrations(campaign.id, {
                    page: registrationPage,
                    size: registrationPageSize,
                    ...registrationFilters
                });
                setRegistrations(result.list || []);
                setRegistrationTotal(result.total || 0);
            }
            setInvoiceUploadOpen(false);
        } catch {
            // 发票上传或刷新失败时保留上传弹窗，并反馈 Vue 页面同类操作错误。
            setInvoiceUploadError("发票上传失败");
        }
    };

    // 按 Vue 报名弹窗回显报名信息，并限制新增参会人不超过当前参会形式配置人数。
    const openRegistrationEditor = (row: CampaignRegistrationVo) => {
        const registration = row.campaignRegistration;
        if (registration?.id == null) return;
        setRegistrationEditRow(row);
        setRegistrationEditMeetingType(registration.meetingType);
        setRegistrationEditFee(
            row.meetingTypeConfig?.find(config => config.type === registration.meetingType)?.registrationFee ??
                registration.registrationFee
        );
        setRegistrationEditCompany(registration.companyLetterhead || "");
        setRegistrationCompanyHeaderOptions([]);
        setRegistrationEditRequirements(registration.meetingRequirements || "");
        setRegistrationEditError(null);
        const editConfig = row.meetingTypeConfig?.find(config => config.type === registration.meetingType);
        const applicants = (row.campaignRegistrationApplicant || []).map(item => ({
            ...(item.id == null ? {} : { id: String(item.id) }),
            position: item.position || "",
            name: item.name || "",
            mobile: item.mobile || ""
        }));
        const hasApplicantFields = editConfig?.fieldConfig?.some(field =>
            ["职务", "姓名", "手机号"].includes(field.fieldName || "")
        );
        setRegistrationEditApplicants(
            !applicants.length &&
                hasApplicantFields &&
                (registration.meetingType === 5 || (editConfig?.maxParticipants || 0) > 1)
                ? [{ position: "", name: "", mobile: "" }]
                : applicants
        );
        setRegistrationEditOpen(true);
    };

    // 按 Vue remote-select 规则查询企业抬头候选，并将候选列表保留在当前编辑弹窗内。
    const searchRegistrationCompanyHeaders = async () => {
        try {
            const result = await loadRegistrationCompanyHeaders({
                page: 1,
                size: 50,
                ...(registrationEditCompany.trim() ? { name: registrationEditCompany.trim() } : {})
            });
            setRegistrationCompanyHeaderOptions(result.list || []);
        } catch {
            setRegistrationEditError("企业抬头候选查询失败");
        }
    };

    const updateRegistrationApplicant = (index: number, field: "position" | "name" | "mobile", value: string) => {
        setRegistrationEditError(null);
        setRegistrationEditApplicants(items =>
            items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item))
        );
    };

    const submitRegistrationEdit = () => {
        const registration = registrationEditRow?.campaignRegistration;
        if (registration?.id == null || campaign?.id == null || registrationEditMeetingType == null) return;
        const isRequired = (fieldName: string) =>
            registrationEditFields.some(field => field.fieldName === fieldName && field.hasRequired);
        if (!registrationEditMeetingType) {
            setRegistrationEditError("请选择参会形式");
            return;
        }
        if (isRequired("企业抬头") && !registrationEditCompany.trim()) {
            setRegistrationEditError("请输入企业抬头");
            return;
        }
        if (isRequired("参会需求") && !registrationEditRequirements.trim()) {
            setRegistrationEditError("请输入参会需求");
            return;
        }
        if (registrationEditApplicants.some(item => isRequired("职务") && !item.position.trim())) {
            setRegistrationEditError("请输入职务");
            return;
        }
        if (registrationEditApplicants.some(item => isRequired("姓名") && !item.name.trim())) {
            setRegistrationEditError("请输入姓名");
            return;
        }
        if (registrationEditApplicants.some(item => item.position.trim().length > 32)) {
            setRegistrationEditError("不超过32个字符");
            return;
        }
        if (registrationEditApplicants.some(item => item.name.trim().length > 32)) {
            setRegistrationEditError("不超过32个字符");
            return;
        }
        if (registrationEditApplicants.some(item => isRequired("手机号") && !item.mobile.trim())) {
            setRegistrationEditError("请输入手机号");
            return;
        }
        if (
            registrationEditApplicants.some(item => item.mobile.trim() && !/^1[3456789]\d{9}$/.test(item.mobile.trim()))
        ) {
            setRegistrationEditError("请输入正确的手机号");
            return;
        }
        const detailForms = registrationEditApplicants
            .filter(item => [item.position, item.name, item.mobile].some(value => value.trim()))
            .map(item => ({
                ...(item.id ? { id: item.id } : {}),
                position: item.position.trim(),
                name: item.name.trim(),
                mobile: item.mobile.trim()
            }));
        const currentUserId = useUserStore.getState().userInfo.user?.id;
        const params: CampaignRegistrationForm = {
            id: Number(registration.id),
            campaignId: Number(campaign.id),
            meetingType: registrationEditMeetingType,
            ...(currentUserId == null ? {} : { applicantId: currentUserId }),
            ...(registrationEditCompany.trim() ? { companyLetterhead: registrationEditCompany.trim() } : {}),
            ...(registrationEditRequirements.trim()
                ? { meetingRequirements: registrationEditRequirements.trim() }
                : {}),
            detailForms
        };
        Modal.confirm({
            cancelText: "取消",
            okText: "确认",
            title: "提示",
            content: "是否确认提交报名信息",
            onOk: async () => {
                try {
                    await updateRegistrationInfo(params);
                    const result = await loadRegistrations(campaign.id as number | string, {
                        page: registrationPage,
                        size: registrationPageSize,
                        ...registrationFilters
                    });
                    setRegistrations(result.list || []);
                    setRegistrationTotal(result.total || 0);
                    setRegistrationEditOpen(false);
                } catch {
                    // 报名信息更新或刷新失败时保留编辑弹窗，并反馈 Vue 页面同类操作错误。
                    setRegistrationEditError("报名信息提交失败");
                }
            }
        });
    };

    // 按 Vue 商品类型保留关联商品详情的历史入口和查询参数。
    const openProductDetail = (product: NonNullable<ActivityProductPageResult["list"]>[number]) => {
        const productId = encodeURIComponent(String(product.productId || ""));
        const productCode = encodeURIComponent(String(product.productCode || ""));
        const time = Date.now();
        switch (product.type) {
            case 1:
                void navigate(`/productManagement/consignment/detail?id=${productId}&time=${time}`);
                break;
            case 2:
                void navigate(`/productManagement/groupPurchase/detail?id=${productId}&time=${time}`);
                break;
            case 3:
                void navigate(`/productManagement/bargain/detail?id=${productId}&time=${time}`);
                break;
            case 4:
                void navigate(`/activityManagement/activeGoodsDetail?code=${productCode}`);
                break;
            default:
                break;
        }
    };

    // 状态操作成功后重新读取详情，避免详情页继续展示旧状态。
    const refreshDetail = async (stateOverride?: number) => {
        if (!id) return;
        const result = await loadActivity(id);
        // Vue 关闭/开启操作完成后以接口最新数据刷新，并兼容后端返回字符串枚举。
        const normalizedCampaign: ActivityRow["campaign"] = {
            ...result.campaign,
            state: stateOverride ?? (result.campaign.state == null ? undefined : Number(result.campaign.state)),
            contentConfigs: result.campaign.contentConfigs?.map(config => Number(config)),
            meetingParticipationConfigs: result.campaign.meetingParticipationConfigs?.map(config => Number(config)),
            needAgreeProtocol:
                result.campaign.needAgreeProtocol == null ? undefined : Number(result.campaign.needAgreeProtocol)
        };
        const normalizedResult: ActivityRow = { ...result, campaign: normalizedCampaign };
        // Vue 操作成功后以最新详情覆盖旧提示，避免成功刷新后仍显示历史错误信息。
        setErrorMessage(null);
        setActivityDetail(normalizedResult);
        setCampaign(normalizedCampaign);
    };

    const handleClose = async () => {
        if (campaign?.id == null) return;
        try {
            await closeActivity(campaign.id);
            await refreshDetail(0);
        } catch {
            setErrorMessage("活动关闭失败");
        }
    };

    const handleOpen = async () => {
        if (campaign?.id == null) return;
        try {
            await openActivity(campaign.id);
            await refreshDetail();
        } catch {
            setErrorMessage("活动开启失败");
        }
    };

    const handleDelete = async () => {
        if (campaign?.id == null) return;
        try {
            await deleteActivity(campaign.id);
            void navigate("/productActivity/activityList");
        } catch {
            setErrorMessage("活动删除失败");
        }
    };

    const registrationEditConfig = registrationEditRow?.meetingTypeConfig?.find(
        config => config.type === registrationEditMeetingType
    );
    const registrationEditFields = registrationEditConfig?.fieldConfig || [];
    const shouldShowRegistrationEditField = (fieldName: string) =>
        !registrationEditFields.length || registrationEditFields.some(field => field.fieldName === fieldName);
    const registrationEditUnlimited = registrationEditMeetingType === 5;
    const registrationEditMaxParticipants = registrationEditUnlimited
        ? Number.POSITIVE_INFINITY
        : Math.max(registrationEditConfig?.maxParticipants || registrationEditApplicants.length || 1, 1);

    const showAssign = async () => {
        if (campaign?.id == null) return;
        try {
            setSpecialKeyword("");
            setSpecials(await loadSpecials(""));
            setSelectedHandlerId(campaign.handlerId == null ? "" : String(campaign.handlerId));
            setAssignOpen(true);
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const searchSpecials = async () => {
        try {
            setSpecials(await loadSpecials(specialKeyword));
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const submitAssign = async () => {
        if (campaign?.id == null || !selectedHandlerId) return;
        try {
            await assignActivity(campaign.id, Number(selectedHandlerId));
            setAssignOpen(false);
            await refreshDetail();
        } catch {
            setErrorMessage("活动指派失败");
        }
    };

    const showOpenButton =
        campaign?.state === 0 &&
        campaign.validTimeStart &&
        campaign.validTimeFinish &&
        new Date(campaign.validTimeStart).getTime() < Date.now() &&
        new Date(campaign.validTimeFinish).getTime() > Date.now();

    // 用 zhihao-ui-react 列契约承接旧 Vue 报名列表字段和报名操作插槽。
    const registrationColumns: TableColumnProps<CampaignRegistrationVo>[] = [
        {
            key: "code",
            label: "报名编号",
            minWidth: 200,
            prop: "code",
            render: ({ row }) => row.campaignRegistration?.code || row.code || "-"
        },
        {
            key: "applicant",
            label: "报名信息",
            width: 300,
            prop: "applicant",
            render: ({ row }) =>
                row.campaignRegistrationApplicant?.length
                    ? row.campaignRegistrationApplicant
                          .map(item => [item.position, item.name, item.mobile].filter(Boolean).join(" / "))
                          .join("；")
                    : "-"
        },
        {
            key: "companyLetterhead",
            label: "企业名称",
            minWidth: 200,
            prop: "companyLetterhead",
            render: ({ row }) => row.campaignRegistration?.companyLetterhead || "-"
        },
        {
            key: "registrationFee",
            label: "报名费总计(元)",
            minWidth: 180,
            prop: "registrationFee",
            render: ({ row }) =>
                row.campaignRegistration?.registrationFee == null
                    ? "-"
                    : `${row.campaignRegistration.registrationFee} 元`
        },
        {
            key: "meetingRequirements",
            label: "参会需求",
            width: 180,
            prop: "meetingRequirements",
            render: ({ row }) => row.campaignRegistration?.meetingRequirements || "-"
        },
        {
            key: "submitterAccount",
            label: "提交账号",
            width: 180,
            prop: "submitterAccount",
            render: ({ row }) => [row.submitterName, row.submitterMobile].filter(Boolean).join(" | ") || "-"
        },
        {
            key: "meetingType",
            label: "参会形式",
            width: 180,
            prop: "meetingType",
            render: ({ row }) => registrationMeetingLabels[row.campaignRegistration?.meetingType || 0] || "-"
        },
        {
            key: "state",
            label: "报名状态",
            width: 160,
            prop: "state",
            render: ({ row }) => registrationStateLabels[row.campaignRegistration?.state || 0] || "-"
        },
        {
            key: "paymentProof",
            label: "支付凭证",
            width: 160,
            prop: "paymentProof",
            render: ({ row }) => {
                const code = row.campaignRegistration?.code || row.code || "";
                return row.campaignRegistration?.paymentVoucherFileIds?.length ? (
                    <Button
                        aria-label={`查看支付凭证${code}`}
                        type="link"
                        onClick={() =>
                            void previewRegistrationFiles(
                                row.campaignRegistration?.paymentVoucherFileIds || [],
                                "查看凭证"
                            )
                        }
                    >
                        查看
                    </Button>
                ) : (
                    <span aria-label="暂无支付凭证">-</span>
                );
            }
        },
        {
            key: "invoiceFileIds",
            label: "发票单据",
            width: 160,
            prop: "invoiceFileIds",
            render: ({ row }) => {
                const code = row.campaignRegistration?.code || row.code || "";
                return row.campaignRegistration?.invoiceFileIds?.length ? (
                    <Button
                        aria-label={`查看发票单据${code}`}
                        type="link"
                        onClick={() =>
                            void previewRegistrationFiles(row.campaignRegistration?.invoiceFileIds || [], "发票单据")
                        }
                    >
                        查看
                    </Button>
                ) : (
                    <span aria-label="暂无发票单据">-</span>
                );
            }
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => {
                if (!canWrite) return null;
                const code = row.campaignRegistration?.code || row.code || "";
                const operationState = getRegistrationOperationState(row.campaignRegistration?.state);
                return (
                    <Space size="small">
                        {operationState.canConfirmPayment ? (
                            <Button
                                aria-label={`确认收款${code}`}
                                onClick={() => confirmRegistrationPayment(row)}
                                type="link"
                            >
                                确认收款
                            </Button>
                        ) : null}
                        {operationState.canEdit ? (
                            <Button
                                aria-label={`修改报名${code}`}
                                onClick={() => openRegistrationEditor(row)}
                                type="link"
                            >
                                修改
                            </Button>
                        ) : null}
                        {operationState.canCancel ? (
                            <Button
                                aria-label={`取消报名${code}`}
                                onClick={() => cancelRegistrationRow(row)}
                                type="link"
                            >
                                取消报名
                            </Button>
                        ) : null}
                        {operationState.canUploadInvoice ? (
                            <Button aria-label={`上传发票${code}`} onClick={() => openInvoiceUpload(row)} type="link">
                                上传发票
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        },
        {
            key: "createdTime",
            label: "报名时间",
            width: 180,
            prop: "createdTime",
            render: ({ row }) => row.campaignRegistration?.createdTime || "-"
        }
    ];

    // 用 zhihao-ui-react 列契约承接旧 Vue 发票抬头列表的嵌套字段映射。
    const invoiceColumns: TableColumnProps<ActivityInvoiceHeaderRow>[] = [
        {
            key: "submitterAccount",
            label: "提交账号",
            width: 300,
            prop: "submitterAccount",
            render: ({ row }) => [row.submitterName, row.submitterMobile].filter(Boolean).join(" | ") || "-"
        },
        {
            key: "headerType",
            label: "抬头类型",
            prop: "headerType",
            render: ({ row }) => invoiceHeaderTypeLabels[getInvoiceHeader(row).headerType || 0] || "-"
        },
        {
            key: "invoiceType",
            label: "发票类型",
            prop: "invoiceType",
            render: ({ row }) => invoiceTypeLabels[getInvoiceHeader(row).invoiceType || 0] || "-"
        },
        {
            key: "name",
            label: "企业名称",
            prop: "name",
            render: ({ row }) => getInvoiceHeader(row).name || "-"
        },
        {
            key: "taxpayerNo",
            label: "纳税人识别号",
            prop: "taxpayerNo",
            render: ({ row }) => getInvoiceHeader(row).taxpayerNo || "-"
        },
        {
            key: "address",
            label: "地址",
            prop: "address",
            render: ({ row }) => getInvoiceHeader(row).address || "-"
        },
        {
            key: "mobile",
            label: "电话",
            prop: "mobile",
            render: ({ row }) => getInvoiceHeader(row).mobile || "-"
        },
        {
            key: "bank",
            label: "开户行",
            prop: "bank",
            render: ({ row }) => getInvoiceHeader(row).bank || "-"
        },
        {
            key: "account",
            label: "账号",
            prop: "account",
            render: ({ row }) => getInvoiceHeader(row).account || "-"
        },
        {
            key: "createdTime",
            label: "提交时间",
            prop: "createdTime",
            render: ({ row }) => getInvoiceHeader(row).createdTime || "-"
        }
    ];

    // 用 zhihao-ui-react 列契约承接旧 Vue 需求对接列表及动态需求字段展示。
    const requirementColumns: TableColumnProps<ActivityRequirementRow>[] = [
        {
            key: "code",
            label: "需求编号",
            // zhihao-ui-react 固定列会触发表头测宽循环，需求编号通过普通首列展示保持可用性。
            width: 180,
            prop: "code",
            render: ({ row }) => row.campaignDemandSubmit?.code || "-"
        },
        {
            key: "remark",
            label: "需求备注",
            minWidth: 200,
            prop: "remark",
            render: ({ row }) => row.campaignDemandSubmit?.demandRemark || "-"
        },
        {
            key: "submitterAccount",
            label: "提交账号",
            minWidth: 200,
            prop: "submitterAccount",
            render: ({ row }) => [row.submitterName, row.submitterMobile].filter(Boolean).join(" | ") || "-"
        },
        {
            key: "fieldValues",
            label: "需求字段",
            prop: "fieldValues",
            render: ({ row }) =>
                row.campaignDemandSubmit?.fieldValues?.length
                    ? row.campaignDemandSubmit.fieldValues.map((field, index) => (
                          <div key={`${field.fieldName || "field"}-${index}`}>
                              {field.fieldName || "-"}：{field.fieldValue || "-"}
                          </div>
                      ))
                    : "-"
        },
        {
            key: "updatedTime",
            label: "提交时间",
            width: 200,
            prop: "updatedTime",
            render: ({ row }) => row.campaignDemandSubmit?.updatedTime || "-"
        }
    ];

    // 用 zhihao-ui-react 列契约承接旧 Vue 活动商品列表、图片展示和详情跳转。
    const productColumns: TableColumnProps<ActivityProductRow>[] = [
        {
            key: "productCode",
            label: "商品编号",
            prop: "productCode",
            render: ({ row }) =>
                row.productCode && [1, 2, 3, 4].includes(row.type || 0) ? (
                    <Button type="link" onClick={() => openProductDetail(row)}>
                        {row.productCode}
                    </Button>
                ) : (
                    row.productCode || "-"
                )
        },
        {
            key: "title",
            label: "主图/标题",
            prop: "title",
            render: ({ row }) => {
                const title = row.product?.title || row.productCampaign?.title || "-";
                return (
                    <Space align="center">
                        {row.mainImgUrl ? (
                            <img
                                alt={title}
                                height={65}
                                src={row.mainImgUrl}
                                style={{ borderRadius: 5, objectFit: "contain", width: 65 }}
                            />
                        ) : (
                            <span aria-label="暂无图片">暂无图片</span>
                        )}
                        <span>{title}</span>
                    </Space>
                );
            }
        },
        {
            key: "productTypeName",
            label: "品类",
            prop: "productTypeName",
            render: ({ row }) => row.product?.productTypeName || "-"
        },
        {
            key: "supplierName",
            label: "供应商",
            prop: "supplierName",
            render: ({ row }) => row.product?.supplierEnterprise?.name || "-"
        },
        {
            key: "type",
            label: "商品类型",
            prop: "type",
            render: ({ row }) => ({ 1: "寄售", 2: "团购", 3: "捡漏", 4: "活动" })[row.type || 0] || "-"
        },
        {
            key: "listingState",
            label: "上架状态",
            prop: "listingState",
            render: ({ row }) => (row.type === 4 ? "上架" : row.product?.state === 1 ? "上架" : "下架")
        },
        { key: "addTime", label: "添加时间", prop: "addTime", render: ({ row }) => row.addTime || "-" }
    ];

    // 用 zhihao-ui-react 列契约承接旧 Vue 凭证预览文件表和文件下载入口。
    const registrationPreviewColumns: TableColumnProps<AdminFile>[] = [
        { key: "name", label: "文件名称", prop: "name", render: ({ row }) => row.name || "-" },
        { key: "type", label: "文件格式", width: 160, prop: "type", render: ({ row }) => row.type || "-" },
        {
            key: "createdTime",
            label: "上传时间",
            width: 160,
            prop: "createdTime",
            render: ({ row }) => row.createdTime || "-"
        },
        {
            key: "operation",
            label: "操作",
            width: 120,
            prop: "operation",
            render: ({ row }) => (
                <a aria-label={`查看/下载${row.name}`} href={row.path} rel="noreferrer" target="_blank">
                    查看/下载
                </a>
            )
        }
    ];

    return (
        <section
            aria-label="activity-detail-page"
            className="activity-detail admin-detail-page detail-page-transparent"
        >
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader
                    leftInfos={[
                        { label: "活动编号", value: campaign?.code || "-" },
                        {
                            label: "活动专员",
                            value:
                                [campaign?.handlerName, campaign?.handlerMobile?.[0] ? formatterPhone(campaign.handlerMobile[0]) : "-"]
                                    .filter(Boolean)
                                    .join(" | ") || "-"
                        }
                    ]}
                    rightInfos={[{ label: "更新时间", value: dateTimeFormat(campaign?.updatedTime) }]}
                    stateDesc={
                        campaign?.state == null ? "-" : activityStateLabels[campaign.state] || String(campaign.state)
                    }
                    title="活动详情"
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {campaign ? (
                    <div aria-label="活动详情标签" className="admin-detail-tabs" role="tablist">
                        {getActivityDetailTabs(campaign.state, campaign.contentConfigs).map(tab => (
                            <button
                                aria-selected={activeDetailTab === tab.key}
                                className={activeDetailTab === tab.key ? "is-active" : undefined}
                                key={tab.key}
                                role="tab"
                                type="button"
                                onClick={() => setActiveDetailTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
            {campaign && activeDetailTab === "base" ? (
                <section className="section-card admin-detail-section" aria-labelledby="activity-info-title">
                    <h2 className="section-title" id="activity-info-title">
                        活动信息
                    </h2>
                    <ZhGrid className="admin-detail-grid" cols={4} gap={[20, 20]}>
                        <ZhBaseItem label="活动标题" span={4} value={campaign.title || "-"} />
                        <ZhBaseItem
                            label="活动时间范围"
                            span={4}
                            value={
                                campaign.validTimeStart || campaign.validTimeFinish
                                    ? `${dateTimeFormat(campaign.validTimeStart)} 至 ${dateTimeFormat(campaign.validTimeFinish)}`
                                    : "--"
                            }
                        />
                        <ZhBaseItem label="会议地址" span={4} value={campaign.meetingAddress || "--"} />
                        <ZhBaseItem
                            label="PC端活动主图"
                            span={4}
                            value={
                                activityMainImages.pc ? (
                                    <div className="admin-detail-media">
                                        <ZhImage alt="PC端活动主图" preview src={activityMainImages.pc.path} />
                                    </div>
                                ) : (
                                    "-"
                                )
                            }
                        />
                        <ZhBaseItem
                            label="移动端活动主图"
                            span={4}
                            value={
                                activityMainImages.mobile ? (
                                    <div className="admin-detail-media">
                                        <ZhImage alt="移动端活动主图" preview src={activityMainImages.mobile.path} />
                                    </div>
                                ) : (
                                    "-"
                                )
                            }
                        />
                    </ZhGrid>
                    <ZhGrid className="admin-detail-content-config mt-5" cols={1} gap={[12, 12]}>
                        <ZhBaseItem
                            label="活动内容配置"
                            value={
                                <span className="admin-detail-tag">
                                    {formatActivityConfigs(campaign.contentConfigs)}
                                </span>
                            }
                        />
                    </ZhGrid>
                </section>
            ) : null}
            {activityDetail && activeDetailTab === "base" ? (
                <>
                    {campaign?.state !== 2 && campaign?.contentConfigs?.includes(1) ? (
                        <section
                            className="section-card admin-detail-section"
                            aria-labelledby="registration-channel-title"
                        >
                            <h2 className="section-title" id="registration-channel-title">
                                报名通道
                            </h2>
                            <ZhGrid cols={1} gap={[12, 12]}>
                                <ZhBaseItem
                                    label="线下收款银行账户"
                                    value={
                                        activityDetail.offlineAccount
                                            ? [
                                                  activityDetail.offlineAccount.name,
                                                  activityDetail.offlineAccount.account,
                                                  activityDetail.offlineAccount.openingBank
                                              ]
                                                  .filter(Boolean)
                                                  .join(" | ") || "-"
                                            : campaign.offlineAccountId || "-"
                                    }
                                />
                                <ZhBaseItem
                                    label="收款码"
                                    value={
                                        activityDetail.paymentQrCodeFile?.path ||
                                        activityDetail.paymentQrCodeFile?.url ? (
                                            <div className="admin-detail-media admin-detail-media--payment">
                                                <ZhImage
                                                    alt="收款码"
                                                    preview
                                                    src={
                                                        activityDetail.paymentQrCodeFile.path ||
                                                        activityDetail.paymentQrCodeFile.url
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            formatActivityFileNames(
                                                activityDetail.paymentQrCodeFile
                                                    ? [activityDetail.paymentQrCodeFile]
                                                    : []
                                            )
                                        )
                                    }
                                />
                                <ZhBaseItem
                                    label="参会形式配置"
                                    value={
                                        campaign.meetingParticipationConfigs?.length ? (
                                            <div className="admin-detail-tags">
                                                {campaign.meetingParticipationConfigs.map(value => (
                                                    <span className="admin-detail-tag" key={value}>
                                                        {participationLabels[value] || String(value)}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            "-"
                                        )
                                    }
                                />
                                <ZhBaseItem
                                    label="报名需同意协议"
                                    value={campaign.needAgreeProtocol === 1 ? "是" : "否"}
                                />
                                {campaign.needAgreeProtocol === 1 ? (
                                    <>
                                        <ZhBaseItem label="协议标题" value={campaign.protocolTitle || "-"} />
                                        <ZhBaseItem
                                            label="协议内容"
                                            value={
                                                campaign.protocolContent ? (
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: campaign.protocolContent
                                                        }}
                                                    />
                                                ) : (
                                                    "-"
                                                )
                                            }
                                        />
                                    </>
                                ) : null}
                            </ZhGrid>
                            {activityDetail.configs?.length ? (
                                <div className="mt-4">
                                    <Typography.Title className="mb-4 mt-0" level={5}>
                                        报名信息配置
                                    </Typography.Title>
                                    {activityDetail.configs.map((config, index) => (
                                        <ActivityRegistrationCard
                                            key={`${config.type || "config"}-${index}`}
                                            config={config}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </section>
                    ) : null}
                    {campaign?.state !== 2 ? (
                        <section className="section-card admin-detail-section" aria-labelledby="homepage-content-title">
                            <h2 className="section-title" id="homepage-content-title">
                                首页内容
                            </h2>
                            <ZhGrid cols={1} gap={[12, 12]}>
                                <ZhBaseItem label="联系方式" value={campaign?.contactInfo || "-"} />
                                <ZhBaseItem
                                    label="日程安排"
                                    value={formatActivityFileNames(
                                        activityDetail.scheduleFile ? [activityDetail.scheduleFile] : []
                                    )}
                                />
                                <ZhBaseItem
                                    label="会议支持"
                                    value={formatActivityFileNames(activityDetail.meetingSupportFiles)}
                                />
                                <ZhBaseItem
                                    label="会议酒店"
                                    value={formatActivityFileNames(activityDetail.conferenceHotelFiles)}
                                />
                                <ZhBaseItem
                                    label="历届风采"
                                    value={formatActivityFileNames(activityDetail.pastGloriesFiles)}
                                />
                            </ZhGrid>
                        </section>
                    ) : null}
                </>
            ) : null}
            {activeDetailTab === "registration" ? (
                <Card className="mt-4" size="small">
                    <ZhTable<CampaignRegistrationVo>
                        data={registrations}
                        columns={normalizeOperationColumns(registrationColumns)}
                        emptyText="暂无报名信息"
                        pagination={false}
                        rowKey={row => String(row.campaignRegistration?.id || row.code)}
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Typography.Title className="mb-4 mt-0" level={5}>
                                    报名信息
                                </Typography.Title>

                                <Form
                                    className="admin-table-filter-form mb-4"
                                    form={registrationForm}
                                    initialValues={{ meetingType: "", state: "" }}
                                    layout="inline"
                                    onFinish={searchRegistrations}
                                >
                                    <Form.Item label="报名编号" name="registrationNo">
                                        <Input placeholder="请输入报名编号搜索" />
                                    </Form.Item>
                                    <Form.Item label="报名筛选关键词" name="keyword">
                                        <Input placeholder="请输入企业名称/职务/姓名" />
                                    </Form.Item>
                                    <Form.Item label="报名手机号" name="mobile">
                                        <Input placeholder="请输入手机号" />
                                    </Form.Item>
                                    <Form.Item label="提交账号" name="submitterKeyword">
                                        <Input placeholder="请输入提交账号/手机号" />
                                    </Form.Item>
                                    <Form.Item label="参会形式" name="meetingType">
                                        <Select
                                            allowClear
                                            aria-label="参会形式"
                                            options={Object.entries(registrationMeetingLabels).map(
                                                ([value, label]) => ({
                                                    label,
                                                    value
                                                })
                                            )}
                                            placeholder="请选择参会形式"
                                        />
                                    </Form.Item>
                                    <Form.Item label="报名状态" name="state">
                                        <Select
                                            allowClear
                                            aria-label="报名状态"
                                            options={Object.entries(registrationStateLabels).map(([value, label]) => ({
                                                label,
                                                value
                                            }))}
                                            placeholder="请选择报名状态"
                                        />
                                    </Form.Item>
                                    <Space>
                                        <Button htmlType="submit" type="primary">
                                            查询报名
                                        </Button>
                                        <Button onClick={resetRegistrationFilters}>重置报名筛选</Button>
                                    </Space>
                                </Form>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={registrationPage}
                                    pageSize={registrationPageSize}
                                    pageSizeOptions={[10, 20, 50]}
                                    showSizeChanger
                                    total={registrationTotal}
                                    // 沿用 Vue 报名分页契约，切换页大小后回到对应页并保留当前筛选条件。
                                    onChange={(page, size) => {
                                        setRegistrationPage(page);
                                        setRegistrationPageSize(size);
                                    }}
                                />
                            </div>
                        }
                    />
                </Card>
            ) : null}
            {activeDetailTab === "invoice" ? (
                <Card className="mt-4" size="small">
                    <ZhTable<ActivityInvoiceHeaderRow>
                        data={invoiceHeaders}
                        columns={normalizeOperationColumns(invoiceColumns)}
                        emptyText="暂无发票抬头"
                        pagination={false}
                        rowKey={row =>
                            String(getInvoiceHeader(row).id || `${row.submitterName}-${row.submitterMobile}`)
                        }
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Typography.Title className="mb-4 mt-0" level={5}>
                                    发票抬头
                                </Typography.Title>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={invoicePage}
                                    pageSize={invoicePageSize}
                                    pageSizeOptions={[10, 20, 50]}
                                    showSizeChanger
                                    total={invoiceTotal}
                                    // 沿用 Vue 发票分页契约，切换页大小后保留当前分页请求参数。
                                    onChange={(page, size) => {
                                        setInvoicePage(page);
                                        setInvoicePageSize(size);
                                    }}
                                />
                            </div>
                        }
                    />
                </Card>
            ) : null}
            {activeDetailTab === "requirement" ? (
                <Card className="mt-4" size="small">
                    <ZhTable<ActivityRequirementRow>
                        data={requirements}
                        columns={normalizeOperationColumns(requirementColumns)}
                        emptyText="暂无需求对接"
                        pagination={false}
                        rowKey={row => String(row.campaignDemandSubmit?.id || row.campaignDemandSubmit?.code)}
                        // 宽表使用 auto 布局，避免 zhihao-ui-react 表头测宽循环更新并保留横向滚动。
                        tableLayout="auto"
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Typography.Title className="mb-4 mt-0" level={5}>
                                    需求对接
                                </Typography.Title>

                                <Form
                                    className="mb-4"
                                    form={requirementForm}
                                    layout="inline"
                                    onFinish={values => searchRequirements(values)}
                                >
                                    <Form.Item label="需求编号" name="demandNo">
                                        <Input placeholder="请输入需求编号搜索" />
                                    </Form.Item>
                                    <Form.Item label="提交账号" name="keyword">
                                        <Input placeholder="请输入提交账号关键字搜索" />
                                    </Form.Item>
                                    <Form.Item label="提交时间" name="updatedTime">
                                        <ZhDateRangePicker
                                            endPlaceholder="提交结束日期"
                                            format="YYYY-MM-DD"
                                            rangeSeparator="-"
                                            startPlaceholder="提交开始日期"
                                            type="daterange"
                                            valueFormat="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                    <Space>
                                        <Button htmlType="submit" type="primary">
                                            查询需求
                                        </Button>
                                        <Button onClick={resetRequirementSearch}>重置</Button>
                                    </Space>
                                </Form>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={requirementPage}
                                    pageSize={requirementPageSize}
                                    pageSizeOptions={[10, 20, 50]}
                                    showSizeChanger
                                    total={requirementTotal}
                                    // 沿用 Vue 需求对接分页契约，切换页大小后保留当前分页请求参数。
                                    onChange={(page, size) => {
                                        setRequirementPage(page);
                                        setRequirementPageSize(size);
                                    }}
                                />
                            </div>
                        }
                    />
                </Card>
            ) : null}
            {campaign?.contentConfigs?.includes(2) && activeDetailTab === "product" ? (
                <Card className="mt-4" size="small">
                    <ZhTable<ActivityProductRow>
                        data={products}
                        columns={normalizeOperationColumns(productColumns)}
                        emptyText="暂无活动商品"
                        pagination={false}
                        rowKey={product => String(product.productId || product.productCode)}
                        isShowResetBtn={false}
                        isShowSearchBtn={false}
                        tableHeader={
                            <div className="admin-table-composed-header">
                                <Typography.Title className="mb-4 mt-0" level={5}>
                                    商品信息
                                </Typography.Title>

                                <Form
                                    className="admin-table-filter-form"
                                    form={productForm}
                                    layout="inline"
                                    onFinish={searchProducts}
                                >
                                    <Form.Item label="商品编号" name="keyword">
                                        <Input placeholder="请输入商品名称/商品编号" />
                                    </Form.Item>
                                    <Form.Item label="品类" name="productTypeName">
                                        <Input placeholder="请输入品类" />
                                    </Form.Item>
                                    <Form.Item label="供应商" name="supplierName">
                                        <Input placeholder="请输入供应商名称" />
                                    </Form.Item>
                                    <Form.Item label="商品类型" name="type">
                                        <Select
                                            allowClear
                                            aria-label="商品类型"
                                            options={[
                                                { label: "寄售", value: "1" },
                                                { label: "团购", value: "2" },
                                                { label: "捡漏", value: "3" },
                                                { label: "活动", value: "4" }
                                            ]}
                                            placeholder="请选择商品类型"
                                        />
                                    </Form.Item>
                                    <Form.Item label="上架状态" name="listingState">
                                        <Select
                                            allowClear
                                            aria-label="上架状态"
                                            options={[
                                                { label: "上架", value: "1" },
                                                { label: "下架", value: "0" }
                                            ]}
                                            placeholder="请选择上架状态"
                                        />
                                    </Form.Item>
                                    <Form.Item label="添加时间" name="addTime">
                                        <ZhDateRangePicker
                                            endPlaceholder="添加结束日期"
                                            format="YYYY-MM-DD"
                                            rangeSeparator="-"
                                            startPlaceholder="添加开始日期"
                                            type="daterange"
                                            valueFormat="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                    <Form.Item>
                                        <Space>
                                            <Button htmlType="submit" type="primary">
                                                查询商品
                                            </Button>
                                            <Button onClick={resetProductFilters}>重置商品筛选</Button>
                                        </Space>
                                    </Form.Item>
                                </Form>
                            </div>
                        }
                        append={
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={productPage}
                                    pageSize={productPageSize}
                                    pageSizeOptions={[10, 20, 50]}
                                    showSizeChanger
                                    total={productTotal}
                                    // 沿用 Vue 活动商品分页契约，切换页大小后保留当前筛选条件。
                                    onChange={(page, size) => {
                                        setProductPage(page);
                                        setProductPageSize(size);
                                    }}
                                />
                            </div>
                        }
                    />
                </Card>
            ) : null}
            <Space className="operation-fixed-btn" size={0}>
                {canWrite ? (
                    <Button
                        aria-label="编辑活动"
                        onClick={() => void navigate(`/productActivity/activity/edit?id=${id || ""}`)}
                    >
                        编辑
                    </Button>
                ) : null}
                {canWrite ? (
                    <Button aria-label={`变更指派活动${campaign?.id || ""}`} onClick={() => void showAssign()}>
                        变更指派
                    </Button>
                ) : null}
                {canWrite && campaign?.state === 1 ? (
                    <Button aria-label={`关闭活动${campaign.id || ""}`} onClick={() => void handleClose()}>
                        关闭
                    </Button>
                ) : null}
                {canWrite && showOpenButton ? (
                    <Button aria-label={`开启活动${campaign.id || ""}`} onClick={() => void handleOpen()}>
                        开启
                    </Button>
                ) : null}
                {canWrite && campaign?.state === 2 ? (
                    <Button aria-label={`删除活动${campaign.id || ""}`} danger onClick={() => void handleDelete()}>
                        删除
                    </Button>
                ) : null}
                <Button aria-label="返回" onClick={() => void navigate("/productActivity/activityList")}>
                    返回
                </Button>
            </Space>
            <ZhDialog
                open={assignOpen}
                modalClass="assign-dialog-modal"
                title="指派专员"
                width="842px"
                onCancel={() => setAssignOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button onClick={() => void submitAssign()} type="primary">
                            确认指派
                        </Button>
                    </Space>
                }
            >
                <div className="assign-dialog-body">
                    <Input
                        aria-label="专员姓名"
                        className="assign-dialog-search"
                        placeholder="请输入姓名"
                        suffix={
                            <button
                                aria-label="搜索专员"
                                className="assign-dialog-search-icon"
                                type="button"
                                onClick={() => void searchSpecials()}
                            >
                                <TableSearchIcon name="search" />
                            </button>
                        }
                        onPressEnter={() => void searchSpecials()}
                        value={specialKeyword}
                        onChange={event => setSpecialKeyword(event.target.value)}
                    />
                    {specials.length ? (
                        <div className="assign-dialog-list">
                            {specials.map(special => {
                                const profile = getActivitySpecialProfile(special);
                                const specialId = profile.id;
                                const selected = specialId != null && String(specialId) === selectedHandlerId;
                                return (
                                    <button
                                        aria-label={`选择${profile.name || "活动专员"}`}
                                        aria-pressed={selected}
                                        className={`assign-dialog-card${selected ? " is-selected" : ""}`}
                                        key={String(specialId)}
                                        type="button"
                                        onClick={() => specialId != null && setSelectedHandlerId(String(specialId))}
                                    >
                                        <div className="assign-dialog-card-info">
                                            <div className="assign-dialog-card-name">
                                                {profile.name || "-"}{" "}
                                                <span className="assign-dialog-employee-id">
                                                    {profile.employeeId || ""}
                                                </span>
                                            </div>
                                            <div className="assign-dialog-follow-up">
                                                跟进的活动数量 <b>{special.productCount ?? ""}</b> 条
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 text-center text-gray-400">暂未设定可指派的服务人员</div>
                    )}
                </div>
            </ZhDialog>
            <ZhDialog
                open={invoiceUploadOpen}
                title="上传发票"
                width={800}
                onCancel={() => setInvoiceUploadOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setInvoiceUploadOpen(false)}>取消</Button>
                        <Button onClick={() => void submitInvoiceUpload()} type="primary">
                            确认上传
                        </Button>
                    </Space>
                }
            >
                {invoiceUploadError ? <Alert message={invoiceUploadError} type="error" /> : null}
                <ZhUpload
                    accept={activityVoucherAccept}
                    action="#"
                    aria-label="选择发票文件"
                    disabled={invoiceUploading}
                    httpRequest={handleInvoiceUpload}
                    limit={10}
                    multiple
                >
                    <Button htmlType="button">上传发票</Button>
                </ZhUpload>
                {invoiceUploadIds ? (
                    <Typography.Text>已上传 {parseActivityFileIds(invoiceUploadIds).length} 个文件</Typography.Text>
                ) : null}
            </ZhDialog>
            <ZhDialog
                open={paymentVoucherUploadOpen}
                title="确认收款"
                width={800}
                onCancel={() => setPaymentVoucherUploadOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setPaymentVoucherUploadOpen(false)}>取消</Button>
                        <Button onClick={submitPaymentVoucherUpload} type="primary">
                            提交支付凭证
                        </Button>
                    </Space>
                }
            >
                <Typography.Text strong>上传支付凭证</Typography.Text>
                <Typography.Paragraph type="secondary">
                    支持最多上传10个文件，单个文件不超过50M（支持上传压缩包）
                </Typography.Paragraph>
                {paymentVoucherUploadError ? <Alert message={paymentVoucherUploadError} type="error" /> : null}
                <ZhUpload
                    accept={activityVoucherAccept}
                    action="#"
                    aria-label="选择支付凭证文件"
                    disabled={paymentVoucherUploading}
                    httpRequest={handlePaymentVoucherUpload}
                    limit={10}
                    multiple
                >
                    <Button htmlType="button">上传支付凭证</Button>
                </ZhUpload>
                {paymentVoucherUploadIds ? (
                    <Typography.Text>
                        已上传 {parseActivityFileIds(paymentVoucherUploadIds).length} 个文件
                    </Typography.Text>
                ) : null}
            </ZhDialog>
            <Modal
                open={registrationEditOpen}
                title="修改报名信息"
                onCancel={() => setRegistrationEditOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setRegistrationEditOpen(false)}>取消</Button>
                        <Button onClick={submitRegistrationEdit} type="primary">
                            提交报名信息
                        </Button>
                    </Space>
                }
            >
                <Space orientation="vertical" className="w-full">
                    {registrationEditError ? <Alert message={registrationEditError} role="alert" type="error" /> : null}
                    <label>
                        参会形式
                        <Select
                            aria-label="参会形式"
                            options={(registrationEditRow?.meetingTypeConfig || []).map(config => ({
                                label: registrationMeetingLabels[config.type || 0] || `参会形式${config.type || "-"}`,
                                value: Number(config.type)
                            }))}
                            value={registrationEditMeetingType}
                            onChange={value => {
                                const nextMeetingType = Number(value);
                                const nextConfig = registrationEditRow?.meetingTypeConfig?.find(
                                    config => config.type === nextMeetingType
                                );
                                setRegistrationEditMeetingType(nextMeetingType);
                                setRegistrationEditFee(nextConfig?.registrationFee);
                                setRegistrationEditCompany("");
                                setRegistrationEditRequirements("");
                                setRegistrationEditApplicants([{ position: "", name: "", mobile: "" }]);
                                setRegistrationEditError(null);
                            }}
                        />
                    </label>
                    <label>
                        报名费
                        <Input
                            aria-label="报名费"
                            readOnly
                            value={registrationEditFee == null ? "" : `${registrationEditFee} 元`}
                        />
                    </label>
                    {shouldShowRegistrationEditField("企业抬头") ? (
                        <Space orientation="vertical" className="w-full">
                            <Input
                                aria-label="企业抬头"
                                placeholder="请输入企业抬头"
                                value={registrationEditCompany}
                                onChange={event => {
                                    setRegistrationEditError(null);
                                    setRegistrationEditCompany(event.target.value);
                                }}
                            />
                            <Button onClick={() => void searchRegistrationCompanyHeaders()}>搜索企业抬头</Button>
                            {registrationCompanyHeaderOptions.length ? (
                                <Select
                                    allowClear
                                    aria-label="企业抬头候选"
                                    options={registrationCompanyHeaderOptions.map(option => ({
                                        label: option.companyName || "-",
                                        value: option.companyName || ""
                                    }))}
                                    placeholder="请选择企业抬头"
                                    value={registrationEditCompany || undefined}
                                    onChange={value => setRegistrationEditCompany(value || "")}
                                />
                            ) : null}
                        </Space>
                    ) : null}
                    {shouldShowRegistrationEditField("参会需求") ? (
                        <Input.TextArea
                            aria-label="参会需求"
                            placeholder="请输入参会需求"
                            value={registrationEditRequirements}
                            onChange={event => {
                                setRegistrationEditError(null);
                                setRegistrationEditRequirements(event.target.value);
                            }}
                        />
                    ) : null}
                    {registrationEditApplicants.map((applicant, index) => (
                        <Space key={applicant.id || index} orientation="vertical" className="w-full">
                            <Typography.Text>参会人员{index + 1}</Typography.Text>
                            {shouldShowRegistrationEditField("职务") ? (
                                <Input
                                    aria-label={`参会人员${index + 1}职务`}
                                    placeholder="请输入职务"
                                    value={applicant.position}
                                    onChange={event =>
                                        updateRegistrationApplicant(index, "position", event.target.value)
                                    }
                                />
                            ) : null}
                            {shouldShowRegistrationEditField("姓名") ? (
                                <Input
                                    aria-label={`参会人员${index + 1}姓名`}
                                    placeholder="请输入姓名"
                                    value={applicant.name}
                                    onChange={event => updateRegistrationApplicant(index, "name", event.target.value)}
                                />
                            ) : null}
                            {shouldShowRegistrationEditField("手机号") ? (
                                <Input
                                    aria-label={`参会人员${index + 1}手机号`}
                                    placeholder="请输入手机号"
                                    value={applicant.mobile}
                                    onChange={event => updateRegistrationApplicant(index, "mobile", event.target.value)}
                                />
                            ) : null}
                            {registrationEditApplicants.length > 1 ? (
                                <Button
                                    aria-label={`删除参会人员${index + 1}`}
                                    onClick={() =>
                                        setRegistrationEditApplicants(items =>
                                            items.filter((_, itemIndex) => itemIndex !== index)
                                        )
                                    }
                                >
                                    删除参会人
                                </Button>
                            ) : null}
                        </Space>
                    ))}
                    {registrationEditApplicants.length < registrationEditMaxParticipants ? (
                        <Button
                            onClick={() =>
                                setRegistrationEditApplicants(items => [
                                    ...items,
                                    { position: "", name: "", mobile: "" }
                                ])
                            }
                        >
                            增加参会人
                        </Button>
                    ) : null}
                </Space>
            </Modal>
            <ZhDialog
                footer={null}
                open={registrationPreviewOpen}
                title={registrationPreviewTitle}
                width={800}
                onCancel={() => setRegistrationPreviewOpen(false)}
            >
                {registrationPreviewError ? (
                    <Alert message={registrationPreviewError} role="alert" type="error" />
                ) : null}
                <ZhTable<AdminFile>
                    data={registrationPreviewFiles}
                    columns={normalizeOperationColumns(registrationPreviewColumns)}
                    emptyText="暂无文件"
                    pagination={false}
                    rowKey={file => String(file.id)}
                />
            </ZhDialog>
        </section>
    );
}

export default ActivityDetailPage;
